import { AdminMessageDto } from '../generated-types/adminMessageDto';
import { MessageContactUsDto } from '../generated-types/messageContactUsDto';
import { MessageDto } from '../generated-types/messageDto';
import { MessageState } from '../generated-types/messageState';
import { MessageStateChangeDto } from '../generated-types/messageStateChangeDto';
import { AbstractApi } from './AbstractApi';

/**
 * An API to handle message-related requests.
 */
export default class MessagesApi extends AbstractApi {
  /**
   * Send a message from the contact us page
   * @param message The message content.
   */
  async ContactUsSendMessage(message: MessageContactUsDto): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('messages/contact-us'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to send message');
    }
  }

  /**
   * Send a message.
   * @param message The message content.
   */
  async SendMessage(message: MessageDto): Promise<void> {
    const response = await this.authFetch(this.BuildUrl('messages'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message),
    });

    if (!response.ok) {
      throw new Error('Failed to send message');
    }
  }

  /**
   * Get all the messages for the admin dashboard
   */
  async GetMessages(): Promise<AdminMessageDto[]> {
    const response = await this.authFetch(this.BuildUrl('messages'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    return response.json() as unknown as AdminMessageDto[];
  }

  /**
   * Delete the specified message
   * @param messageId The message to delete
   */
  async DeleteMessage(messageId: string): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl(`messages/${messageId}`),
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) throw Error();
  }

  /**
   * Update the message state
   * @param messageId The id of the message to update the state for
   * @param state The state to set it to
   */
  async ChangeState(messageId: string, state: MessageState): Promise<string> {
    const updateDto: MessageStateChangeDto = {
      messageState: state,
    };
    const response = await this.authFetch(
      this.BuildUrl(`messages/${messageId}/state`),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateDto),
      }
    );

    if (!response.ok) throw Error();
    return response.text();
  }

  /**
   * Get the number of unread messages
   * @returns The number of unread messages
   */
  async GetUnreadMessages(): Promise<number> {
    const response = await this.authFetch(
      this.BuildUrl('messages/unread-count'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return response.text() as unknown as number;
  }
}
